import type { Character, Word } from '@ezishizi/content-schema';
import type { Rng } from './rng.ts';
import { pickN } from './rng.ts';

export const canShowPicture = (w: Word) =>
  w.kind !== 'abstract' && (w.imageId !== null || w.emoji !== null);

// Candidate pools here run to hundreds/thousands of characters and this same handful of pinyin
// readings recurs across every one of them, so memoise by the input string: normalize('NFD') and
// friends were showing up as the hot path of round-building at ~1000 candidates x 20 questions.
const stripTonesCache = new Map<string, string>();
const stripTones = (p: string): string => {
  const cached = stripTonesCache.get(p);
  if (cached !== undefined) return cached;
  const v = p.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ü/g, 'v');
  stripTonesCache.set(p, v);
  return v;
};

/** Toned reading, NFC-normalised and lower-cased, for exact-homophone comparison. Memoised for
 * the same reason as `stripTones`. */
const nfcLowerCache = new Map<string, string>();
const nfcLower = (p: string): string => {
  const cached = nfcLowerCache.get(p);
  if (cached !== undefined) return cached;
  const v = p.normalize('NFC').toLowerCase();
  nfcLowerCache.set(p, v);
  return v;
};

/** A word's syllables without tone marks, lower-cased: "xiǎo yú" → ["xiao", "yu"]. Memoised per
 * `pinyin` string: soundsAlike is called per candidate too, over the same word pools. */
const tonelessSyllablesCache = new Map<string, string[]>();
const tonelessSyllables = (w: Pick<Word, 'pinyin'>): string[] => {
  const cached = tonelessSyllablesCache.get(w.pinyin);
  if (cached) return cached;
  const v = w.pinyin.trim().toLowerCase().split(/\s+/).map(stripTones);
  tonelessSyllablesCache.set(w.pinyin, v);
  return v;
};

/**
 * True when `w` would be a guess against `target` by ear: the whole word sounds the same apart
 * from tones (他 / 她 / 它, all tā), or it contains the syllable being taught (小鱼 yú beside
 * 下雨 yǔ). A tone is the only thing separating them, and a first-year learner can't hear that
 * yet; tone contrasts belong to the Tone exercise, not to a listening question.
 */
export function soundsAlike(
  target: Pick<Word, 'pinyin'>,
  w: Pick<Word, 'pinyin'>,
  targetSyllable: string | null = null,
): boolean {
  const a = tonelessSyllables(target);
  const b = tonelessSyllables(w);
  if (a.join(' ') === b.join(' ')) return true;
  if (targetSyllable) return b.includes(stripTones(targetSyllable.toLowerCase()));
  return false;
}

export interface PictureDistractorOptions {
  /** How many of the nearest eligible candidates to draw from. */
  window?: number;
  /** The taught character's syllable inside the target word ("yú" for 鱼 in 小鱼): no distractor
   * may carry it in any tone, nor sound like the whole word (see `soundsAlike`). */
  avoidSyllable?: string | null;
}

/**
 * Picture distractors: candidates are passed in priority order (same set, same lesson, level).
 * Never the same text, meaning or sound; pick from the nearest `window` eligible items.
 */
export function pictureDistractors(
  target: Word,
  candidates: readonly Word[],
  n: number,
  rng: Rng,
  opts: PictureDistractorOptions = {},
): Word[] {
  const window = opts.window ?? 8;
  const seen = new Set<string>([target.text, target.meaningEn.toLowerCase()]);
  const eligible: Word[] = [];
  for (const w of candidates) {
    if (w.id === target.id || !canShowPicture(w)) continue;
    if (soundsAlike(target, w, opts.avoidSyllable ?? null)) continue;
    const m = w.meaningEn.toLowerCase();
    if (seen.has(w.text) || seen.has(m)) continue;
    seen.add(w.text);
    seen.add(m);
    eligible.push(w);
    if (eligible.length >= window) break;
  }
  return pickN(eligible, n, rng);
}

export interface HanziDistractorOptions {
  /** Characters of the current lesson; a small boost, since the child has seen them. */
  sameLesson?: ReadonlySet<string>;
  /** Default true: the same syllable in another tone (母 for 木) is the best distractor for a
   * reading or pinyin question. False for a listening question at this level: the child has to
   * pick by ear, and a tone is not yet something they can hear, so such a character is dropped
   * rather than ranked first. */
  toneContrast?: boolean;
}

/**
 * Hanzi distractors ranked by confusability: same syllable with a different tone, same radical,
 * similar stroke count, then same-lesson neighbours. Weighted random among the top. A character
 * that sounds exactly the same as the target (any shared toned reading: 河 / 和 / 合, all hé) is
 * never offered: these questions are answered from the sound or the pinyin, so a true homophone
 * would leave the child guessing between two right answers.
 */
export function hanziDistractors(
  target: Character,
  candidates: readonly Character[],
  n: number,
  rng: Rng,
  opts: HanziDistractorOptions = {},
): string[] {
  const { sameLesson = new Set<string>(), toneContrast = true } = opts;
  const tPy = new Set(target.pinyin.map(stripTones));
  const tToned = new Set(target.pinyin.map(nfcLower));
  // Each candidate's toneless/toned readings are derived once here rather than inside every
  // filter/map pass below (rng() still runs exactly once per candidate that reaches .map, in the
  // same order, so output is unchanged).
  const scored = candidates
    .filter((c) => c.char !== target.char)
    .map((c) => ({ c, toneless: c.pinyin.map(stripTones), toned: c.pinyin.map(nfcLower) }))
    .filter(({ toned }) => !toned.some((p) => tToned.has(p)))
    .filter(({ toneless }) => toneContrast || !toneless.some((p) => tPy.has(p)))
    .map(({ c, toneless }) => {
      let s = 0;
      if (toneless.some((p) => tPy.has(p))) s += 3;
      if (c.radical === target.radical) s += 2;
      if (Math.abs(c.strokeCount - target.strokeCount) <= 1) s += 1;
      if (sameLesson.has(c.char)) s += 1;
      return { c: c.char, s: s + rng() * 0.5 };
    })
    .sort((a, b) => b.s - a.s);
  return scored.slice(0, n).map((x) => x.c);
}
